import { useContext, Component } from 'react';
import { Redirect } from 'react-router';
import Modal from 'react-bootstrap/Modal';
import { Container } from 'reactstrap';
import { Button } from 'reactstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import { DeviceEntries } from './DeviceEntries';
import { ModalDeviceAddOrUpdate } from './ModalDeviceAddOrUpdate';
import * as Utils from '../../../utils/helper';
import * as Misc from '../../../utils/misc';
import * as apiCalls from '../../../logic/apiCalls';
import { urls } from '../../../logic/urls';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import { Alert } from 'react-bootstrap';

// The maximum allowed number of devices per tenant.
const maximumNumberOfDevices = 500;

class DeviceSettingsInternal extends Component {
    refreshDevicesTimer = 0;
    refreshDevicesTimeoutTimer = 0;
    nameToAdd = '';

    state = {
        isLoading: true,
        devices: [],
        deviceToDelete: {},
        deviceToUpdate: {},
        isConfirmDeleteModalOpen: false,
        isDeviceModalOpen: false,
        isDeviceModalUpdate: false,
        redirectToMainDevicePage: false,
        islicenseAvailable: false,
        isSubscriptionAvailable: false,
    }

    async componentDidMount() {
        if (this.props.match.params.deviceid) {
            this.nameToAdd = this.props.match.params.deviceid;
            this.setState({
                isDeviceModalUpdate: false,
                isDeviceModalOpen: true,
            });
        }

        this.getDevices();
    }

    async componentWillUnmount() {
        this.refreshDevicesTimerStop();
    }

    showConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: true,
        });
    };

    hideConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: false,
        });
    };

    showAddDeviceModal = () => {
        this.setState({
            isDeviceModalUpdate: false,
            isDeviceModalOpen: true,
            islicenseAvailable: (this.props.tenantContext.totalLicenseCount - this.state.devices.length) > 0,
            isSubscriptionAvailable: this.props.tenantContext.totalLicenseCount > 0,
        });
    };

    showUpdateDeviceModal = () => {
        this.setState({
            isDeviceModalUpdate: true,
            isDeviceModalOpen: true,
        });
    };

    hideDeviceModal = () => {
        this.nameToAdd = '';
        this.setState({
            isDeviceModalOpen: false,
        });

        this.setState({
            redirectToMainDevicePage: true,
        });
    }

    refreshDevicesTimerStart = () => {
        console.log("[refreshDevicesTimerStart]");

        // Clear any existing timers to prevent orphaned timers.
        clearInterval(this.refreshDevicesTimer);
        clearInterval(this.refreshDevicesTimeoutTimer);
        
        this.refreshDevicesTimer = setInterval(this.handleRefreshDevicesTimer, 10 * 1000);
        this.refreshDevicesTimeoutTimer = setInterval(this.refreshDevicesTimerStop, 2 * 60 * 60 * 1000)
    }

    refreshDevicesTimerStop = () => {
        console.log("[refreshDevicesTimerStop]");

        clearInterval(this.refreshDevicesTimer)
        clearInterval(this.refreshDevicesTimeoutTimer)
    }

    handleRefreshDevicesTimer = () => {
        this.refreshDevicesStatus();
    }
    
    getDevices = async () => {
        try {
            console.log("[getDevices] Invoking the back-end API to get all devices of this tenant.");

            const devices = await apiCalls.getDevices(this.props.authenticationContext, this.props.tenantContext.tenantId);

            this.setState({
                devices: devices,
            });               
        }
        catch (err) {
            console.log(`[getDevices] Error retrieving the devices: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    handleDeviceDelete = (device) => {
        this.setState({
            deviceToDelete: device,
        });
        this.showConfirmDeleteModal();
    }

    handleDeviceEdit = (device) => {
        this.setState({
            deviceToUpdate: device,
        });
        this.showUpdateDeviceModal();
    }

    executeDeleteDevice = async () => {
        try {
            console.log(`[deleteDevice] Invoking the back-end API to delete the device with Id ${this.state.deviceToDelete.deviceId}.`);

            await apiCalls.deleteDevice(this.props.authenticationContext, this.props.tenantContext.tenantId, this.state.deviceToDelete.deviceId);

            this.props.notificationContext.setNotification("Deleting device.", `'${this.state.deviceToDelete.deviceDescription}' successfully deleted.`, 'success');

            this.getDevices();
        }
        catch (err) {
            this.props.notificationContext.setNotification("Deleting device.", `Error deleting '${this.state.deviceToDelete.deviceDescription}'. ${err.toDetailedMessage()}`, 'danger');                  
        }

        this.hideConfirmDeleteModal();
    }

    executeAddOrUpdate = async (doneInfo) => {
        const deviceAction = doneInfo.isUpdate ? 'updating' : 'adding';
        const deviceActionDone = doneInfo.isUpdate ? 'updated' : 'added';

        try {
            console.log(`[executeAddOrUpdate] Invoking the back-end API ${deviceAction} the device.`);

            if (doneInfo.isUpdate)
            {
                await apiCalls.updateDevice(this.props.authenticationContext, this.props.tenantContext.tenantId, doneInfo.deviceId, doneInfo.name, doneInfo.description, doneInfo.groupId);
            }
            else
            {
                await apiCalls.addDevice(this.props.authenticationContext, this.props.tenantContext.tenantId, doneInfo.name, doneInfo.description, doneInfo.groupId);
            }

            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(deviceAction)} device.`, `'${doneInfo.description}' successfully ${deviceActionDone}.`, 'success');
            this.getDevices();
        }
        catch (err) {
            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(deviceAction)} device.`, `Error ${deviceAction} '${doneInfo.description}'. ${err.toDetailedMessage()}`, 'danger');
        }
        finally {
            this.hideDeviceModal();
        }
    }

    shouldAddDeviceButtonBeEnabled = () => {
        if (this.state.devices.length >= maximumNumberOfDevices) {
            return false;
        }

        return true;
    }

    // ------------------------------
    // Rendering starts here...
    // ------------------------------
    render () {

        if (this.state.redirectToMainDevicePage) {
            this.setState({
                redirectToMainDevicePage: false,
            });
            
            return <Redirect to={urls.device} />
        }

        const headerRendered = <h1>Device settings</h1> 

        if (this.state.isLoading) {
            return (
                <Container fluid>
                    {headerRendered}
                    {this.props.tenantContext.showWarningWhenDisabled()}
                    <p><em>Loading...</em></p>
                </Container>
            );
        }

        const warningText = Utils.isTrue(this.state.deviceToDelete.enabled) ? `The device '${this.state.deviceToDelete.deviceDescription}' will no longer be able to use the Robin service.` : "";
        
        const confirmDeleteModalRendered = <>
            <Modal show={this.state.isConfirmDeleteModalOpen} onHide={this.hideConfirmDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete device '{this.state.deviceToDelete.deviceDescription}'?<br/>    
                    {warningText}           
                </Modal.Body>
                <Modal.Footer>
                    <Button color="secondary" onClick={this.hideConfirmDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={this.executeDeleteDevice}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

        const directDevices = this.state.devices.filter(device => !device.sipTrunkId);

        const directDevicesRendered = directDevices.length > 0 ? 
            <DeviceEntries tenant={this.props.tenantContext} devices={directDevices} handleDelete={this.handleDeviceDelete} handleEdit={this.handleDeviceEdit}/> : 
            <Alert className="mt-3" variant='warning' transition={false}>
                No devices configured. Please add a device.
            </Alert>;

        return (
            <Container fluid>
                {confirmDeleteModalRendered}
                <ModalDeviceAddOrUpdate isOpen={this.state.isDeviceModalOpen} isUpdate={this.state.isDeviceModalUpdate} tenant={this.props.tenantContext} deviceToUpdate={this.state.deviceToUpdate} nameToAdd={this.nameToAdd} onHide={this.hideDeviceModal} onAddOrUpdate={this.executeAddOrUpdate} onScan={this.showDeviceScanModal} isTrunked={false} islicenseAvailable={this.state.islicenseAvailable} isSubscriptionAvailable={this.state.isSubscriptionAvailable}/>
                {headerRendered}
                {this.props.tenantContext.showWarningWhenDisabled()}
                <hr/>
                <ButtonWithDisabledTooltip color="primary" disabled={!this.shouldAddDeviceButtonBeEnabled()} disabledTooltip="Maximum number of devices reached" onClick={this.showAddDeviceModal}>Add device</ButtonWithDisabledTooltip>
                {directDevicesRendered}
                <br/>
            </Container>
        );
    }
}

// Wrap the component so that multiple contexts are available via the props.
export const DeviceSettings = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <DeviceSettingsInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}